// src/core/Message/Message.tsx
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import LoadingBar from "@cloudscape-design/chat-components/loading-bar";
import * as React from "react";
import { useAppTranslation } from "@amzn/awsmp-max-translations";
import { MessageRole, MessageStatus } from "@amzn/awsmp-max-model";

// src/core/Message/Message.module.scss
var Message_module_default = {
  fadeIn: "Message_module_fadeIn",
  fadeInExpand: "Message_module_fadeInExpand",
  fadeInContent: "Message_module_fadeInContent",
  message: "Message_module_message",
  tabContent: "Message_module_tabContent",
  messageAssistant: "Message_module_messageAssistant",
  contentBox: "Message_module_contentBox",
  messageUser: "Message_module_messageUser",
  stopButtonContainer: "Message_module_stopButtonContainer",
  stopButtonText: "Message_module_stopButtonText",
  loadingBarContainer: "Message_module_loadingBarContainer",
  undoButtonContainer: "Message_module_undoButtonContainer"
};

// src/core/Message/Message.tsx
import { Fragment as Fragment2, jsx, jsxs } from "react/jsx-runtime";
var Message = ({ data, children, onInterrupt, onUndo }) => {
  const { t } = useAppTranslation();
  const { messageId, role, status } = data;
  const isAssistant = role === MessageRole.Assistant;
  const isStreaming = status === MessageStatus.Streaming;
  const isComplete = status === MessageStatus.Complete;
  const [userSelectedTab, setUserSelectedTab] = React.useState(false);
  const [initialTabSet, setInitialTabSet] = React.useState(false);
  const resultsBlocks = React.useMemo(() => children.filter((b) => b.category === "results"), [children]);
  const analysisBlocks = React.useMemo(() => children.filter((b) => b.category === "analysis"), [children]);
  const hasAnalysisContent = analysisBlocks.length > 0;
  const defaultActiveTabId = React.useMemo(() => {
    if (status === MessageStatus.Streaming) {
      return "analysis";
    }
    return "results";
  }, [status]);
  const [activeTabId, setActiveTabId] = React.useState(defaultActiveTabId);
  const prevMessageIdRef = React.useRef(messageId);
  React.useEffect(() => {
    if (prevMessageIdRef.current !== messageId) {
      prevMessageIdRef.current = messageId;
      setUserSelectedTab(false);
      setInitialTabSet(false);
    }
  }, [messageId]);
  const prevStatusRef = React.useRef(status);
  React.useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;
    if (prevStatus === MessageStatus.Streaming && status === MessageStatus.Complete) {
      setUserSelectedTab(false);
      setActiveTabId(defaultActiveTabId);
    }
  }, [status, defaultActiveTabId]);
  React.useEffect(() => {
    if (!userSelectedTab && status === MessageStatus.Streaming && !initialTabSet) {
      setActiveTabId(defaultActiveTabId);
      setInitialTabSet(true);
    }
  }, [userSelectedTab, status, initialTabSet, defaultActiveTabId, messageId, activeTabId]);
  const handleTabChange = React.useCallback(({ detail }) => {
    setActiveTabId(detail.activeTabId);
    setUserSelectedTab(true);
  }, []);
  const tabs = React.useMemo(() => {
    return [
      {
        id: "results",
        label: t("results"),
        disabled: status !== MessageStatus.Complete || resultsBlocks.length === 0,
        content: /* @__PURE__ */ jsx(Box, { padding: { top: "m" }, className: Message_module_default.tabContent, children: /* @__PURE__ */ jsx(SpaceBetween, { size: "s", children: resultsBlocks.map((block) => /* @__PURE__ */ jsx(React.Fragment, { children: block.component }, block.blockId)) }) })
      },
      {
        id: "analysis",
        label: t("analysisSteps"),
        disabled: !hasAnalysisContent,
        content: /* @__PURE__ */ jsx(Box, { padding: { top: "m" }, className: Message_module_default.tabContent, children: /* @__PURE__ */ jsx(SpaceBetween, { size: "xxxs", children: analysisBlocks.map((block) => /* @__PURE__ */ jsx(React.Fragment, { children: block.component }, block.blockId)) }) })
      }
    ];
  }, [resultsBlocks, hasAnalysisContent, analysisBlocks, status]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      "data-component": "Message",
      "data-message-id": messageId,
      "data-role": role,
      className: `${Message_module_default.message} ${isAssistant ? Message_module_default.messageAssistant : Message_module_default.messageUser}`,
      children: /* @__PURE__ */ jsxs("div", { className: Message_module_default.contentBox, children: [
        isAssistant && isStreaming && onInterrupt && /* @__PURE__ */ jsxs("div", { className: Message_module_default.stopButtonContainer, children: [
          /* @__PURE__ */ jsxs(Box, { variant: "span", fontSize: "body-s", fontWeight: "light", className: Message_module_default.stopButtonText, children: [
            t("stopProcessing"),
            " (Esc)"
          ] }),
          /* @__PURE__ */ jsx(Button, { variant: "icon", iconName: "status-stopped", ariaLabel: t("stopProcessing"), onClick: onInterrupt })
        ] }),
        isAssistant ? /* @__PURE__ */ jsxs(Fragment2, { children: [
          /* @__PURE__ */ jsx(Tabs, { disableContentPaddings: true, activeTabId, onChange: handleTabChange, tabs }),
          status === MessageStatus.Streaming && /* @__PURE__ */ jsx("div", { className: Message_module_default.loadingBarContainer, children: /* @__PURE__ */ jsx(LoadingBar, { variant: "gen-ai" }) }),
          isComplete && onUndo && /* @__PURE__ */ jsx("div", { className: Message_module_default.undoButtonContainer, children: /* @__PURE__ */ jsx(Button, { variant: "icon", iconName: "undo", ariaLabel: t("undoLastMessage"), onClick: onUndo }) })
        ] }) : (
          /* User messages - render all blocks inline */
          /* @__PURE__ */ jsx(SpaceBetween, { size: "m", children: children.map((block) => /* @__PURE__ */ jsx(React.Fragment, { children: block.component }, block.blockId)) })
        )
      ] })
    }
  );
};

// src/core/ContentBlock/ContentBlock.tsx
import * as React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var ContentBlock = ({ data, children, layoutManager: _, navigationManager: __ }) => {
  const { contentBlockId, category = "display", type, role } = data;
  const childrenWithRole = React2.Children.map(children, (child) => {
    if (React2.isValidElement(child)) {
      return React2.cloneElement(child, { role });
    }
    return child;
  });
  return /* @__PURE__ */ jsx2("div", { "data-component": "ContentBlock", "data-content-block-id": contentBlockId, "data-category": category, "data-type": type, "data-role": role, children: childrenWithRole });
};

// src/core/Text/Text.tsx
import * as React3 from "react";
import { useCitations } from "@amzn/awsmp-max-ux-patterns/Citations";
import { MarkdownRenderer } from "@amzn/awsmp-max-ux-patterns/MarkdownRenderer";
import { Fragment as Fragment4, jsx as jsx3 } from "react/jsx-runtime";
var Text = ({ data, messageRole }) => {
  const { content, citations, citationPattern } = data;
  const textValue = content?.text ?? "";
  const hasCitations = Boolean(citations && citations.length > 0);
  const emptyCitations = React3.useMemo(() => [], []);
  const { processText } = useCitations(citations || emptyCitations, {
    interactionMode: "popup",
    pattern: citationPattern,
    fontSize: "compact"
  });
  const processChildren = React3.useCallback(
    (children) => {
      if (!hasCitations) {
        return children;
      }
      if (typeof children === "string") {
        const processed = processText(children);
        if (Array.isArray(processed)) {
          return processed.length === 1 ? processed[0] : /* @__PURE__ */ jsx3(Fragment4, { children: processed });
        }
        return processed;
      }
      if (Array.isArray(children)) {
        return children.map((child, index) => /* @__PURE__ */ jsx3(React3.Fragment, { children: processChildren(child) }, index));
      }
      return children;
    },
    [hasCitations, processText]
  );
  return /* @__PURE__ */ jsx3(MarkdownRenderer, { variant: messageRole, processChildren, children: textValue });
};

// src/core/InlineThink/InlineThink.tsx
var InlineThink = () => {
  return null;
};

// src/core/NotificationBlock/NotificationBlock.tsx
import { useEffect as useEffect2 } from "react";
import Alert from "@cloudscape-design/components/alert";
import { jsx as jsx4 } from "react/jsx-runtime";
var NotificationBlock = ({ data, logger }) => {
  const isInterrupt = "interrupt" in data.content;
  let message;
  let type;
  let header;
  if (isInterrupt) {
    message = data.content.interrupt.message;
    type = "warning";
    header = "Processing Interrupted";
  } else {
    message = data.content.error.message;
    type = "error";
    header = "Error Occurred";
  }
  useEffect2(() => {
    if (isInterrupt) {
      logger.warn("Notification: Processing interrupted", { message });
      logger.opsMetric({
        metricName: "NotificationBlockInterruptCount",
        unit: "Count",
        value: 1,
        dimensions: {
          NotificationType: "interrupt",
          Component: "NotificationBlock"
        }
      });
    } else {
      logger.error("Notification: Error occurred", void 0, { message });
      logger.opsMetric({
        metricName: "NotificationBlockErrorCount",
        unit: "Count",
        value: 1,
        dimensions: {
          Component: "NotificationBlock"
        }
      });
    }
  }, []);
  return /* @__PURE__ */ jsx4(Alert, { type, header, children: /* @__PURE__ */ jsx4("div", { children: message }) });
};

// src/results/LinkToArtifact/LinkToArtifact.tsx
import * as React6 from "react";
import ContentLayout from "@cloudscape-design/components/content-layout";
import SpaceBetween3 from "@cloudscape-design/components/space-between";
import Button2 from "@cloudscape-design/components/button";
import { ArtifactThumbnailContainer } from "@amzn/awsmp-max-ux-patterns/ArtifactThumbnailContainer";
import { ArtifactActions } from "@amzn/awsmp-max-ux-patterns/ArtifactActions";
import { exportAsPDF, exportAsCSV } from "@amzn/awsmp-max-ux-patterns/DocumentExporter";
import { useAppTranslation as useAppTranslation3 } from "@amzn/awsmp-max-translations";

// src/results/LinkToArtifact/ComparisonActions.tsx
import * as React5 from "react";
import SpaceBetween2 from "@cloudscape-design/components/space-between";
import Box2 from "@cloudscape-design/components/box";
import { ClickableOptions } from "@amzn/awsmp-max-ux-patterns/ClickableOptions";
import { ProductSelector } from "@amzn/awsmp-max-ux-patterns/ProductSelector";
import { useAppTranslation as useAppTranslation2 } from "@amzn/awsmp-max-translations";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var ComparisonActions = ({
  artifact,
  searchArtifact,
  maxService,
  sendMessage,
  flashbar,
  promptInputActions,
  logger
}) => {
  const { t, currentLanguage } = useAppTranslation2();
  const [showProductSelector, setShowProductSelector] = React5.useState(false);
  const [selectedProducts, setSelectedProducts] = React5.useState([]);
  const artifactProducts = React5.useMemo(() => {
    if (!artifact || artifact.artifactType !== "comparison" || !artifact.products)
      return [];
    return artifact.products.map((p) => ({
      productId: p.productId,
      productName: p.productName,
      vendorName: p.vendorName,
      productLogoUrl: p.productLogoUrl
    }));
  }, [artifact]);
  const recommendedProducts = React5.useMemo(() => {
    if (!searchArtifact || searchArtifact.artifactType !== "search")
      return [];
    const recommendations = searchArtifact.recommendations;
    if (!recommendations || !Array.isArray(recommendations))
      return [];
    return recommendations.map((r) => ({
      productId: r.productId,
      productName: r.productName,
      vendorName: r.vendorName,
      productLogoUrl: r.logoUrl
    }));
  }, [searchArtifact]);
  const otherConsideredProducts = React5.useMemo(() => {
    if (!searchArtifact || searchArtifact.artifactType !== "search")
      return [];
    const otherProducts = searchArtifact.otherProductsConsidered;
    if (!otherProducts || !Array.isArray(otherProducts))
      return [];
    return otherProducts.map((p) => ({
      productId: p.productId,
      productName: p.productName || "",
      vendorName: p.vendorName || "",
      productLogoUrl: void 0
      // Other products don't have logo URLs
    }));
  }, [searchArtifact]);
  const isMatchingExistingComparison = React5.useMemo(() => {
    if (selectedProducts.length === 0)
      return false;
    if (!artifact || artifact.artifactType !== "comparison" || !artifact.products)
      return false;
    const existingIds = artifact.products.map((p) => p.productId).sort();
    const selectedIds = selectedProducts.map((p) => p.productId).sort();
    return existingIds.length === selectedIds.length && existingIds.every((id, index) => id === selectedIds[index]);
  }, [selectedProducts, artifact]);
  const handleProductSelectorError = React5.useCallback(
    (errorMessage) => {
      flashbar.addItem({
        type: "error",
        header: t("searchFailed"),
        content: errorMessage,
        autoDismiss: true,
        dismissTimeout: 5e3
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  if (!artifact || artifact.artifactType !== "comparison") {
    return null;
  }
  const handleOptionClick = (option) => {
    if (option === t("editProducts")) {
      setShowProductSelector(true);
    } else if (option === t("addCriteria")) {
      promptInputActions.insertTemplate(t("addCriteriaTemplate"));
    }
  };
  const handleProductSelectionConfirm = (products) => {
    const removedProducts = artifactProducts.filter((original) => !products.some((p) => p.productId === original.productId));
    const addedProducts = products.filter((newProduct) => !artifactProducts.some((p) => p.productId === newProduct.productId));
    const formatProducts = (prods) => {
      const productNames = prods.map((p) => p.productName);
      const listFormatter = new Intl.ListFormat(currentLanguage, {
        style: "long",
        type: "conjunction"
      });
      return listFormatter.format(productNames);
    };
    let message;
    if (removedProducts.length > 0 && addedProducts.length > 0) {
      message = t("removeAndAddProducts", {
        removedProducts: formatProducts(removedProducts),
        addedProducts: formatProducts(addedProducts)
      });
    } else if (addedProducts.length > 0) {
      message = t("addProductsToComparison", { products: formatProducts(addedProducts) });
    } else if (removedProducts.length > 0) {
      message = t("removeProductsFromComparison", { products: formatProducts(removedProducts) });
    } else {
      message = t("updateComparisonWithProducts", { products: formatProducts(products) });
    }
    const listingIds = products.map((p) => p.productId);
    sendMessage({
      contentBlocks: [{ text: message }],
      agent: {
        agentId: "comparison",
        listingIds
      }
    });
    setShowProductSelector(false);
  };
  return /* @__PURE__ */ jsxs2(SpaceBetween2, { size: "m", children: [
    /* @__PURE__ */ jsxs2(SpaceBetween2, { size: "xs", children: [
      /* @__PURE__ */ jsx5(Box2, { variant: "h5", fontWeight: "bold", children: t("updateComparison") }),
      /* @__PURE__ */ jsx5(
        ClickableOptions,
        {
          options: [t("editProducts"), t("addCriteria")],
          onSelect: handleOptionClick,
          ariaLabel: t("artifactActions"),
          logger
        }
      )
    ] }),
    showProductSelector && /* @__PURE__ */ jsx5(
      ProductSelector,
      {
        maxService,
        initialProducts: artifactProducts,
        onChange: setSelectedProducts,
        disabled: isMatchingExistingComparison,
        onConfirm: handleProductSelectionConfirm,
        onCancel: () => setShowProductSelector(false),
        onError: handleProductSelectorError,
        recommendedProducts,
        otherConsideredProducts
      }
    )
  ] });
};

// src/results/LinkToArtifact/LinkToArtifact.module.scss
var LinkToArtifact_module_default = {
  titleContainer: "LinkToArtifact_module_titleContainer",
  title: "LinkToArtifact_module_title"
};

// src/results/LinkToArtifact/LinkToArtifact.tsx
import {
  ProductSearch,
  ProductSearchThumbnail,
  ProductComparison,
  ProductComparisonThumbnail,
  ProductComparisonAsStandaloneReport,
  ProductEvaluation,
  ProductEvaluationThumbnail,
  ArtifactFallback,
  convertToCSV
} from "@amzn/awsmp-max-agentic-ui-artifacts";
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var ProductSearchCompact = (props) => /* @__PURE__ */ jsx6(ProductSearchThumbnail, { data: props.data, logger: props.logger });
var ProductComparisonCompact = (props) => /* @__PURE__ */ jsx6(ProductComparisonThumbnail, { data: props.data });
var ProductEvaluationCompact = (props) => /* @__PURE__ */ jsx6(ProductEvaluationThumbnail, { data: props.data });
var ARTIFACT_COMPONENTS = {
  search: ProductSearchCompact,
  comparison: ProductComparisonCompact,
  evaluation: ProductEvaluationCompact,
  fallback: ArtifactFallback
};
function createSplitPanelArtifactComponents() {
  const SearchWithActions = (props) => {
    const setTitle = React6.useCallback(
      (title) => {
        props.layoutManager.splitPanel.setContent({
          header: title
        });
      },
      [props.layoutManager]
    );
    React6.useEffect(() => {
      const sizeFactor = 0.5;
      props.layoutManager.splitPanel.updateMetadata({ size: Math.floor(window.innerWidth * sizeFactor) });
    }, []);
    return /* @__PURE__ */ jsxs3(ContentLayout, { children: [
      /* @__PURE__ */ jsx6("div", { style: { display: "flex", justifyContent: "flex-end", marginBottom: "16px" }, children: /* @__PURE__ */ jsx6(ArtifactActions, { onExpand: props.onExpand }) }),
      /* @__PURE__ */ jsx6(ProductSearch, { ...props, setTitle, variant: "compact", mode: "edit" })
    ] });
  };
  const ComparisonWithActions = (props) => {
    const { t } = useAppTranslation3();
    const handleDownloadCSV = async () => {
      await exportAsCSV(convertToCSV(props.data, t), `product-comparison.csv`);
    };
    const handleDownloadPDF = async () => {
      await exportAsPDF(/* @__PURE__ */ jsx6(ProductComparisonAsStandaloneReport, { data: props.data, title: "Product Comparison" }), `product-comparison.pdf`);
    };
    React6.useEffect(() => {
      const sizeFactor = 0.5;
      props.layoutManager.splitPanel.updateMetadata({ size: Math.floor(window.innerWidth * sizeFactor) });
    }, []);
    return /* @__PURE__ */ jsxs3(ContentLayout, { children: [
      /* @__PURE__ */ jsx6("div", { style: { display: "flex", justifyContent: "flex-end", marginBottom: "16px" }, children: /* @__PURE__ */ jsx6(ArtifactActions, { onDownloadCSV: handleDownloadCSV, onDownloadPDF: handleDownloadPDF, onExpand: props.onExpand }) }),
      /* @__PURE__ */ jsx6(ProductComparison, { ...props })
    ] });
  };
  const EvaluationWithActions = (props) => {
    const handleDownloadPDF = async () => {
      await exportAsPDF(/* @__PURE__ */ jsx6(ProductEvaluation, { ...props }), `${props.data.metadata.reportTitle}.pdf`);
    };
    React6.useEffect(() => {
      const sizeFactor = 0.5;
      props.layoutManager.splitPanel.updateMetadata({ size: Math.floor(window.innerWidth * sizeFactor) });
    }, []);
    return /* @__PURE__ */ jsxs3(ContentLayout, { children: [
      /* @__PURE__ */ jsx6("div", { style: { display: "flex", justifyContent: "flex-end", marginBottom: "16px" }, children: /* @__PURE__ */ jsx6(ArtifactActions, { onDownloadPDF: handleDownloadPDF, onExpand: props.onExpand }) }),
      /* @__PURE__ */ jsx6(ProductEvaluation, { ...props })
    ] });
  };
  return {
    search: SearchWithActions,
    comparison: ComparisonWithActions,
    evaluation: EvaluationWithActions,
    fallback: ArtifactFallback
  };
}
var LinkToArtifact = ({ data, rendererAPI }) => {
  const { t } = useAppTranslation3();
  const {
    windowSize,
    projectId,
    renderArtifactInSplitPanel,
    renderArtifacts,
    getArtifact,
    navigationManager,
    maxService,
    sendMessage,
    layoutManager,
    promptInputActions,
    logger
  } = rendererAPI;
  const SPLIT_PANEL_COMPONENTS = React6.useMemo(() => createSplitPanelArtifactComponents(), []);
  const toolData = data.input;
  const artifactType = toolData?.artifactSection;
  const artifact = artifactType ? getArtifact(artifactType) : void 0;
  const searchArtifact = getArtifact("search");
  if (!toolData || !artifactType)
    return null;
  const getArtifactTypeLabel = (type) => {
    const labels = {
      search: t("recommendedProducts"),
      comparison: t("productComparison"),
      evaluation: t("productEvaluation"),
      epoSellerCreateConfiguration: "Express Private Offer Seller Configuration",
      epoBuyerRequest: "Express Private Offer Buyer Request",
      epoDiscount: "Express Private Offer Discount",
      memory: "Memory"
    };
    return labels[type] || type;
  };
  const handleOpenInSplitPanel = artifact ? () => {
    if (windowSize !== "l") {
      navigationManager.navigate("artifact", { projectId, artifactType });
    } else {
      const component = SPLIT_PANEL_COMPONENTS[artifactType] || SPLIT_PANEL_COMPONENTS.fallback;
      renderArtifactInSplitPanel({ [artifactType]: component });
    }
  } : void 0;
  const artifactToRender = artifact || {
    artifactId: `placeholder-${artifactType}`,
    artifactType,
    name: "",
    json: {}
  };
  const rendered = renderArtifacts([artifactToRender], ARTIFACT_COMPONENTS);
  const artifactContent = rendered[artifactType];
  const handleViewClick = artifact ? () => {
    logger.biMetric({
      metricName: "Click",
      unit: "Count",
      value: 1,
      dimensions: {
        ClickAct: "Click",
        ClickSelect: "view-artifact-button",
        ClickObj: "Button",
        ClickFeat: "LinkToArtifact"
      }
    });
    navigationManager.navigate("artifact", { projectId, artifactType });
  } : void 0;
  const comparisonActions = data.output ? /* @__PURE__ */ jsx6(
    ComparisonActions,
    {
      artifact,
      searchArtifact,
      maxService,
      sendMessage,
      flashbar: layoutManager.flashbar,
      promptInputActions,
      logger
    }
  ) : null;
  const titleElement = /* @__PURE__ */ jsxs3("div", { className: LinkToArtifact_module_default.titleContainer, children: [
    /* @__PURE__ */ jsx6("div", { className: LinkToArtifact_module_default.title, children: getArtifactTypeLabel(artifactType) }),
    artifact && /* @__PURE__ */ jsx6(Button2, { variant: "normal", onClick: handleViewClick, children: "View" })
  ] });
  return /* @__PURE__ */ jsxs3(SpaceBetween3, { size: "m", children: [
    /* @__PURE__ */ jsx6(ArtifactThumbnailContainer, { title: titleElement, onClick: handleOpenInSplitPanel, artifactContent }),
    comparisonActions
  ] });
};

// src/results/WebSearch/WebSearch.tsx
import { useRef as useRef2 } from "react";

// ../../node_modules/motion/dist/es/motion-utils/dist/es/noop.mjs
var noop = /* @__NO_SIDE_EFFECTS__ */ (any) => any;

// ../../node_modules/motion/dist/es/motion-utils/dist/es/errors.mjs
var warning = noop;
var invariant = noop;
if (true) {
  warning = (check, message) => {
    if (!check && typeof console !== "undefined") {
      console.warn(message);
    }
  };
  invariant = (check, message) => {
    if (!check) {
      throw new Error(message);
    }
  };
}

// ../../node_modules/motion/dist/es/motion-utils/dist/es/memo.mjs
// @__NO_SIDE_EFFECTS__
function memo(callback) {
  let result;
  return () => {
    if (result === void 0)
      result = callback();
    return result;
  };
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/utils/supports/scroll-timeline.mjs
var supportsScrollTimeline = memo(() => window.ScrollTimeline !== void 0);

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/controls/BaseGroup.mjs
var BaseGroupPlaybackControls = class {
  constructor(animations) {
    this.stop = () => this.runAll("stop");
    this.animations = animations.filter(Boolean);
  }
  get finished() {
    return Promise.all(this.animations.map((animation) => "finished" in animation ? animation.finished : animation));
  }
  /**
   * TODO: Filter out cancelled or stopped animations before returning
   */
  getAll(propName) {
    return this.animations[0][propName];
  }
  setAll(propName, newValue) {
    for (let i = 0; i < this.animations.length; i++) {
      this.animations[i][propName] = newValue;
    }
  }
  attachTimeline(timeline, fallback) {
    const subscriptions = this.animations.map((animation) => {
      if (supportsScrollTimeline() && animation.attachTimeline) {
        return animation.attachTimeline(timeline);
      } else if (typeof fallback === "function") {
        return fallback(animation);
      }
    });
    return () => {
      subscriptions.forEach((cancel, i) => {
        cancel && cancel();
        this.animations[i].stop();
      });
    };
  }
  get time() {
    return this.getAll("time");
  }
  set time(time) {
    this.setAll("time", time);
  }
  get speed() {
    return this.getAll("speed");
  }
  set speed(speed) {
    this.setAll("speed", speed);
  }
  get startTime() {
    return this.getAll("startTime");
  }
  get duration() {
    let max = 0;
    for (let i = 0; i < this.animations.length; i++) {
      max = Math.max(max, this.animations[i].duration);
    }
    return max;
  }
  runAll(methodName) {
    this.animations.forEach((controls) => controls[methodName]());
  }
  flatten() {
    this.runAll("flatten");
  }
  play() {
    this.runAll("play");
  }
  pause() {
    this.runAll("pause");
  }
  cancel() {
    this.runAll("cancel");
  }
  complete() {
    this.runAll("complete");
  }
};

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/controls/Group.mjs
var GroupPlaybackControls = class extends BaseGroupPlaybackControls {
  then(onResolve, onReject) {
    return Promise.all(this.animations).then(onResolve).catch(onReject);
  }
};

// ../../node_modules/motion/dist/es/motion-utils/dist/es/progress.mjs
var progress = /* @__NO_SIDE_EFFECTS__ */ (from, to, value) => {
  const toFromDifference = to - from;
  return toFromDifference === 0 ? 1 : (value - from) / toFromDifference;
};

// ../../node_modules/motion/dist/es/motion-utils/dist/es/time-conversion.mjs
var secondsToMilliseconds = /* @__NO_SIDE_EFFECTS__ */ (seconds) => seconds * 1e3;
var millisecondsToSeconds = /* @__NO_SIDE_EFFECTS__ */ (milliseconds) => milliseconds / 1e3;

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/generators/utils/calc-duration.mjs
var maxGeneratorDuration = 2e4;
function calcGeneratorDuration(generator) {
  let duration = 0;
  const timeStep = 50;
  let state2 = generator.next(duration);
  while (!state2.done && duration < maxGeneratorDuration) {
    duration += timeStep;
    state2 = generator.next(duration);
  }
  return duration >= maxGeneratorDuration ? Infinity : duration;
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/generators/utils/create-generator-easing.mjs
function createGeneratorEasing(options, scale = 100, createGenerator) {
  const generator = createGenerator({ ...options, keyframes: [0, scale] });
  const duration = Math.min(calcGeneratorDuration(generator), maxGeneratorDuration);
  return {
    type: "keyframes",
    ease: (progress2) => {
      return generator.next(duration * progress2).value / scale;
    },
    duration: millisecondsToSeconds(duration)
  };
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/generators/utils/is-generator.mjs
function isGenerator(type) {
  return typeof type === "function";
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/utils/resolve-elements.mjs
function resolveElements(elementOrSelector, scope, selectorCache) {
  var _a;
  if (elementOrSelector instanceof Element) {
    return [elementOrSelector];
  } else if (typeof elementOrSelector === "string") {
    let root = document;
    if (scope) {
      root = scope.current;
    }
    const elements = (_a = selectorCache === null || selectorCache === void 0 ? void 0 : selectorCache[elementOrSelector]) !== null && _a !== void 0 ? _a : root.querySelectorAll(elementOrSelector);
    return elements ? Array.from(elements) : [];
  }
  return Array.from(elementOrSelector);
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/utils/get-value-transition.mjs
function getValueTransition(transition, key) {
  return transition ? transition[key] || transition["default"] || transition : void 0;
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/waapi/utils/attach-timeline.mjs
function attachTimeline(animation, timeline) {
  animation.timeline = timeline;
  animation.onfinish = null;
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/waapi/NativeAnimationControls.mjs
var NativeAnimationControls = class {
  constructor(animation) {
    this.animation = animation;
  }
  get duration() {
    var _a, _b, _c;
    const durationInMs = ((_b = (_a = this.animation) === null || _a === void 0 ? void 0 : _a.effect) === null || _b === void 0 ? void 0 : _b.getComputedTiming().duration) || ((_c = this.options) === null || _c === void 0 ? void 0 : _c.duration) || 300;
    return millisecondsToSeconds(Number(durationInMs));
  }
  get time() {
    var _a;
    if (this.animation) {
      return millisecondsToSeconds(((_a = this.animation) === null || _a === void 0 ? void 0 : _a.currentTime) || 0);
    }
    return 0;
  }
  set time(newTime) {
    if (this.animation) {
      this.animation.currentTime = secondsToMilliseconds(newTime);
    }
  }
  get speed() {
    return this.animation ? this.animation.playbackRate : 1;
  }
  set speed(newSpeed) {
    if (this.animation) {
      this.animation.playbackRate = newSpeed;
    }
  }
  get state() {
    return this.animation ? this.animation.playState : "finished";
  }
  get startTime() {
    return this.animation ? this.animation.startTime : null;
  }
  get finished() {
    return this.animation ? this.animation.finished : Promise.resolve();
  }
  play() {
    this.animation && this.animation.play();
  }
  pause() {
    this.animation && this.animation.pause();
  }
  stop() {
    if (!this.animation || this.state === "idle" || this.state === "finished") {
      return;
    }
    if (this.animation.commitStyles) {
      this.animation.commitStyles();
    }
    this.cancel();
  }
  flatten() {
    var _a;
    if (!this.animation)
      return;
    (_a = this.animation.effect) === null || _a === void 0 ? void 0 : _a.updateTiming({ easing: "linear" });
  }
  attachTimeline(timeline) {
    if (this.animation)
      attachTimeline(this.animation, timeline);
    return noop;
  }
  complete() {
    this.animation && this.animation.finish();
  }
  cancel() {
    try {
      this.animation && this.animation.cancel();
    } catch (e) {
    }
  }
};

// ../../node_modules/motion/dist/es/motion-dom/dist/es/utils/supports/flags.mjs
var supportsFlags = {
  linearEasing: void 0
};

// ../../node_modules/motion/dist/es/motion-dom/dist/es/utils/supports/memo.mjs
function memoSupports(callback, supportsFlag) {
  const memoized = memo(callback);
  return () => {
    var _a;
    return (_a = supportsFlags[supportsFlag]) !== null && _a !== void 0 ? _a : memoized();
  };
}

// ../../node_modules/motion/dist/es/motion-dom/dist/es/utils/supports/linear-easing.mjs
var supportsLinearEasing = /* @__PURE__ */ memoSupports(() => {
  try {
    document.createElement("div").animate({ opacity: 0 }, { easing: "linear(0, 1)" });
  } catch (e) {
    return false;
  }
  return true;
}, "linearEasing");

// ../../node_modules/motion/dist/es/motion-dom/dist/es/utils/is-bezier-definition.mjs
var isBezierDefinition = (easing) => Array.isArray(easing) && typeof easing[0] === "number";

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/waapi/utils/linear.mjs
var generateLinearEasing = (easing, duration, resolution = 10) => {
  let points = "";
  const numPoints = Math.max(Math.round(duration / resolution), 2);
  for (let i = 0; i < numPoints; i++) {
    points += easing(progress(0, numPoints - 1, i)) + ", ";
  }
  return `linear(${points.substring(0, points.length - 2)})`;
};

// ../../node_modules/motion/dist/es/motion-dom/dist/es/animation/waapi/utils/easing.mjs
var cubicBezierAsString = ([a, b, c, d]) => `cubic-bezier(${a}, ${b}, ${c}, ${d})`;
var supportedWaapiEasing = {
  linear: "linear",
  ease: "ease",
  easeIn: "ease-in",
  easeOut: "ease-out",
  easeInOut: "ease-in-out",
  circIn: /* @__PURE__ */ cubicBezierAsString([0, 0.65, 0.55, 1]),
  circOut: /* @__PURE__ */ cubicBezierAsString([0.55, 0, 1, 0.45]),
  backIn: /* @__PURE__ */ cubicBezierAsString([0.31, 0.01, 0.66, -0.59]),
  backOut: /* @__PURE__ */ cubicBezierAsString([0.33, 1.53, 0.69, 0.99])
};
function mapEasingToNativeEasing(easing, duration) {
  if (!easing) {
    return void 0;
  } else if (typeof easing === "function" && supportsLinearEasing()) {
    return generateLinearEasing(easing, duration);
  } else if (isBezierDefinition(easing)) {
    return cubicBezierAsString(easing);
  } else if (Array.isArray(easing)) {
    return easing.map((segmentEasing) => mapEasingToNativeEasing(segmentEasing, duration) || supportedWaapiEasing.easeOut);
  } else {
    return supportedWaapiEasing[easing];
  }
}

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/index.mjs
function startWaapiAnimation(element, valueName, keyframes, { delay = 0, duration = 300, repeat = 0, repeatType = "loop", ease = "easeInOut", times } = {}) {
  const keyframeOptions = { [valueName]: keyframes };
  if (times)
    keyframeOptions.offset = times;
  const easing = mapEasingToNativeEasing(ease, duration);
  if (Array.isArray(easing))
    keyframeOptions.easing = easing;
  return element.animate(keyframeOptions, {
    delay,
    duration,
    easing: !Array.isArray(easing) ? easing : "linear",
    fill: "both",
    iterations: repeat + 1,
    direction: repeatType === "reverse" ? "alternate" : "normal"
  });
}

// ../../node_modules/motion/dist/es/framer-motion/dist/es/value/types/numbers/units.mjs
var createUnitType = (unit) => ({
  test: (v) => typeof v === "string" && v.endsWith(unit) && v.split(" ").length === 1,
  parse: parseFloat,
  transform: (v) => `${v}${unit}`
});
var percent = /* @__PURE__ */ createUnitType("%");
var px = /* @__PURE__ */ createUnitType("px");
var progressPercentage = {
  ...percent,
  parse: (v) => percent.parse(v) / 100,
  transform: (v) => percent.transform(v * 100)
};

// ../../node_modules/motion/dist/es/framer-motion/dist/es/render/dom/value-types/number-browser.mjs
var browserNumberValueTypes = {
  // Border props
  borderWidth: px,
  borderTopWidth: px,
  borderRightWidth: px,
  borderBottomWidth: px,
  borderLeftWidth: px,
  borderRadius: px,
  radius: px,
  borderTopLeftRadius: px,
  borderTopRightRadius: px,
  borderBottomRightRadius: px,
  borderBottomLeftRadius: px,
  // Positioning props
  width: px,
  maxWidth: px,
  height: px,
  maxHeight: px,
  top: px,
  right: px,
  bottom: px,
  left: px,
  // Spacing props
  padding: px,
  paddingTop: px,
  paddingRight: px,
  paddingBottom: px,
  paddingLeft: px,
  margin: px,
  marginTop: px,
  marginRight: px,
  marginBottom: px,
  marginLeft: px,
  // Misc
  backgroundPositionX: px,
  backgroundPositionY: px
};

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/utils/get-final-keyframe.mjs
var isNotNull = (value) => value !== null;
function getFinalKeyframe(keyframes, { repeat, repeatType = "loop" }, finalKeyframe) {
  const resolvedKeyframes = keyframes.filter(isNotNull);
  const index = repeat && repeatType !== "loop" && repeat % 2 === 1 ? 0 : resolvedKeyframes.length - 1;
  return !index || finalKeyframe === void 0 ? resolvedKeyframes[index] : finalKeyframe;
}

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/utils/style.mjs
function setCSSVar(element, name, value) {
  element.style.setProperty(`--${name}`, value);
}
function setStyle(element, name, value) {
  element.style[name] = value;
}

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/utils/supports-partial-keyframes.mjs
var supportsPartialKeyframes = /* @__PURE__ */ memo(() => {
  try {
    document.createElement("div").animate({ opacity: [1] });
  } catch (e) {
    return false;
  }
  return true;
});

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/utils/supports-waapi.mjs
var supportsWaapi = /* @__PURE__ */ memo(() => Object.hasOwnProperty.call(Element.prototype, "animate"));

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/NativeAnimation.mjs
var state = /* @__PURE__ */ new WeakMap();
function hydrateKeyframes(valueName, keyframes, read) {
  for (let i = 0; i < keyframes.length; i++) {
    if (keyframes[i] === null) {
      keyframes[i] = i === 0 ? read() : keyframes[i - 1];
    }
    if (typeof keyframes[i] === "number" && browserNumberValueTypes[valueName]) {
      keyframes[i] = browserNumberValueTypes[valueName].transform(keyframes[i]);
    }
  }
  if (!supportsPartialKeyframes() && keyframes.length < 2) {
    keyframes.unshift(read());
  }
}
var defaultEasing = "easeOut";
function getElementAnimationState(element) {
  const animationState = state.get(element) || /* @__PURE__ */ new Map();
  state.set(element, animationState);
  return state.get(element);
}
var NativeAnimation = class extends NativeAnimationControls {
  constructor(element, valueName, valueKeyframes, options) {
    const isCSSVar = valueName.startsWith("--");
    invariant(typeof options.type !== "string", `animateMini doesn't support "type" as a string. Did you mean to import { spring } from "framer-motion"?`);
    const existingAnimation = getElementAnimationState(element).get(valueName);
    existingAnimation && existingAnimation.stop();
    const readInitialKeyframe = () => {
      return valueName.startsWith("--") ? element.style.getPropertyValue(valueName) : window.getComputedStyle(element)[valueName];
    };
    if (!Array.isArray(valueKeyframes)) {
      valueKeyframes = [valueKeyframes];
    }
    hydrateKeyframes(valueName, valueKeyframes, readInitialKeyframe);
    if (isGenerator(options.type)) {
      const generatorOptions = createGeneratorEasing(options, 100, options.type);
      options.ease = supportsLinearEasing() ? generatorOptions.ease : defaultEasing;
      options.duration = secondsToMilliseconds(generatorOptions.duration);
      options.type = "keyframes";
    } else {
      options.ease = options.ease || defaultEasing;
    }
    const onFinish = () => {
      this.setValue(element, valueName, getFinalKeyframe(valueKeyframes, options));
      this.cancel();
      this.resolveFinishedPromise();
    };
    const init = () => {
      this.setValue = isCSSVar ? setCSSVar : setStyle;
      this.options = options;
      this.updateFinishedPromise();
      this.removeAnimation = () => {
        const elementState = state.get(element);
        elementState && elementState.delete(valueName);
      };
    };
    if (!supportsWaapi()) {
      super();
      init();
      onFinish();
    } else {
      super(startWaapiAnimation(element, valueName, valueKeyframes, options));
      init();
      if (options.autoplay === false) {
        this.animation.pause();
      }
      this.animation.onfinish = onFinish;
      getElementAnimationState(element).set(valueName, this);
    }
  }
  /**
   * Allows the returned animation to be awaited or promise-chained. Currently
   * resolves when the animation finishes at all but in a future update could/should
   * reject if its cancels.
   */
  then(resolve, reject) {
    return this.currentFinishedPromise.then(resolve, reject);
  }
  updateFinishedPromise() {
    this.currentFinishedPromise = new Promise((resolve) => {
      this.resolveFinishedPromise = resolve;
    });
  }
  play() {
    if (this.state === "finished") {
      this.updateFinishedPromise();
    }
    super.play();
  }
  cancel() {
    this.removeAnimation();
    super.cancel();
  }
};

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/animate-elements.mjs
function animateElements(elementOrSelector, keyframes, options, scope) {
  const elements = resolveElements(elementOrSelector, scope);
  const numElements = elements.length;
  invariant(Boolean(numElements), "No valid element provided.");
  const animations = [];
  for (let i = 0; i < numElements; i++) {
    const element = elements[i];
    const elementTransition = { ...options };
    if (typeof elementTransition.delay === "function") {
      elementTransition.delay = elementTransition.delay(i, numElements);
    }
    for (const valueName in keyframes) {
      const valueKeyframes = keyframes[valueName];
      const valueOptions = {
        ...getValueTransition(elementTransition, valueName)
      };
      valueOptions.duration = valueOptions.duration ? secondsToMilliseconds(valueOptions.duration) : valueOptions.duration;
      valueOptions.delay = secondsToMilliseconds(valueOptions.delay || 0);
      animations.push(new NativeAnimation(element, valueName, valueKeyframes, valueOptions));
    }
  }
  return animations;
}

// ../../node_modules/motion/dist/es/framer-motion/dist/es/animation/animators/waapi/animate-style.mjs
var createScopedWaapiAnimate = (scope) => {
  function scopedAnimate(elementOrSelector, keyframes, options) {
    return new GroupPlaybackControls(animateElements(elementOrSelector, keyframes, options, scope));
  }
  return scopedAnimate;
};
var animateMini = /* @__PURE__ */ createScopedWaapiAnimate();

// src/results/WebSearch/WebSearch.tsx
import { MarkdownRenderer as MarkdownRenderer3 } from "@amzn/awsmp-max-ux-patterns/MarkdownRenderer";
import { useCitations as useCitations3 } from "@amzn/awsmp-max-ux-patterns/Citations";
import { useAppTranslation as useAppTranslation4 } from "@amzn/awsmp-max-translations";

// src/results/WebSearch/WebSearchDocument.tsx
import { MarkdownRenderer as MarkdownRenderer2 } from "@amzn/awsmp-max-ux-patterns/MarkdownRenderer";
import { useCitations as useCitations2 } from "@amzn/awsmp-max-ux-patterns/Citations";

// src/results/WebSearch/WebSearch.module.css
var WebSearch_default = {
  stackedCardsContainer: "WebSearch_stackedCardsContainer",
  cardsWrapper: "WebSearch_cardsWrapper",
  countBadge: "WebSearch_countBadge",
  webSearchCard: "WebSearch_webSearchCard",
  hovered: "WebSearch_hovered",
  cardHeader: "WebSearch_cardHeader",
  cardLabel: "WebSearch_cardLabel",
  citationBadge: "WebSearch_citationBadge",
  cardContent: "WebSearch_cardContent",
  documentContainer: "WebSearch_documentContainer",
  documentPage: "WebSearch_documentPage",
  documentHeader: "WebSearch_documentHeader",
  documentTitle: "WebSearch_documentTitle",
  documentSubtitle: "WebSearch_documentSubtitle",
  documentContent: "WebSearch_documentContent",
  citationsSection: "WebSearch_citationsSection",
  citationsTitle: "WebSearch_citationsTitle",
  citationsList: "WebSearch_citationsList",
  citationItem: "WebSearch_citationItem",
  citationLink: "WebSearch_citationLink",
  citationNumber: "WebSearch_citationNumber",
  citationTitle: "WebSearch_citationTitle"
};

// src/results/WebSearch/WebSearchDocument.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
function toStringValue(value) {
  return typeof value === "string" ? value : value.join("");
}
var WebSearchDocument = ({ data }) => {
  const content = toStringValue(data.content);
  const citations = data.citations;
  const normalizedCitations = citations.map((citation, idx) => ({
    id: idx + 1,
    title: toStringValue(citation.title),
    url: toStringValue(citation.url)
  }));
  const { processText } = useCitations2(normalizedCitations, {
    interactionMode: "popup",
    fontSize: "compact"
  });
  return /* @__PURE__ */ jsx7("div", { className: WebSearch_default.documentContainer, children: /* @__PURE__ */ jsx7("div", { className: WebSearch_default.documentPage, children: /* @__PURE__ */ jsx7("div", { className: WebSearch_default.documentContent, children: /* @__PURE__ */ jsx7(MarkdownRenderer2, { processChildren: (children) => typeof children === "string" ? processText(children) : children, children: content }) }) }) });
};

// src/results/WebSearch/WebSearch.tsx
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
function toStringValue2(value) {
  return typeof value === "string" ? value : value.join("");
}
function normalizeCitations(citations) {
  return citations.map((citation, index) => ({
    id: index + 1,
    title: toStringValue2(citation.title),
    url: toStringValue2(citation.url)
  }));
}
function truncateText(text, maxLength) {
  if (text.length <= maxLength)
    return text;
  return text.substring(0, maxLength).trim() + "...";
}
var WebSearch = ({ data, rendererAPI }) => {
  const { t } = useAppTranslation4();
  const cardRef = useRef2(null);
  const { layoutManager } = rendererAPI;
  const toolData = data.output;
  const normalizedCitations = toolData ? normalizeCitations(toolData.citations) : [];
  const { processText } = useCitations3(normalizedCitations, {
    interactionMode: "link",
    fontSize: "compact"
  });
  if (!toolData)
    return null;
  const { content, citations } = toolData;
  const textContent = toStringValue2(content);
  const citationCount = citations.length;
  const compactContent = truncateText(textContent, 150);
  const handleMouseEnter = () => {
    if (cardRef.current) {
      animateMini(cardRef.current, { transform: "translateY(-4px) scale(1.02)" }, { duration: 0.2, ease: "easeOut" });
    }
  };
  const handleMouseLeave = () => {
    if (cardRef.current) {
      animateMini(cardRef.current, { transform: "translateY(0px) scale(1)" }, { duration: 0.2, ease: "easeOut" });
    }
  };
  const handleClick = () => {
    layoutManager.splitPanel.setContent({
      header: t("webSearchResults", { count: citationCount }),
      content: /* @__PURE__ */ jsx8(WebSearchDocument, { data: toolData })
    });
    layoutManager.splitPanel.show();
    layoutManager.splitPanel.updateMetadata({ size: window.innerWidth * 0.5 });
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      ref: cardRef,
      className: WebSearch_default.webSearchCard,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      role: "button",
      tabIndex: 0,
      "aria-label": t("webSearchAriaLabel", { count: citationCount }),
      children: [
        /* @__PURE__ */ jsxs4("div", { className: WebSearch_default.cardHeader, children: [
          /* @__PURE__ */ jsx8("span", { className: WebSearch_default.cardLabel, children: t("webSearch") }),
          /* @__PURE__ */ jsx8("span", { className: WebSearch_default.citationBadge, children: t("sourcesCount", { count: citationCount }) })
        ] }),
        /* @__PURE__ */ jsx8("div", { className: WebSearch_default.cardContent, ...{ inert: "" }, children: /* @__PURE__ */ jsx8(MarkdownRenderer3, { processChildren: (children) => typeof children === "string" ? processText(children) : children, children: compactContent }) })
      ]
    }
  );
};

// src/results/WebSearch/SourcesList.tsx
import SpaceBetween4 from "@cloudscape-design/components/space-between";
import Link from "@cloudscape-design/components/link";
import Box3 from "@cloudscape-design/components/box";
import { jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var SourcesList = ({ citations }) => {
  if (citations.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs5(SpaceBetween4, { size: "xs", children: [
    /* @__PURE__ */ jsx9(Box3, { variant: "h4", children: "Web Search Sources" }),
    /* @__PURE__ */ jsx9("ol", { style: { margin: 0, paddingLeft: "24px" }, children: citations.map((citation) => /* @__PURE__ */ jsx9("li", { style: { marginBottom: "8px" }, children: /* @__PURE__ */ jsx9(Link, { href: citation.url, external: true, fontSize: "body-m", children: citation.title }) }, citation.id)) })
  ] });
};

// src/results/PricingAnnotation/PricingAnnotation.tsx
import Alert2 from "@cloudscape-design/components/alert";
import Link2 from "@cloudscape-design/components/link";
import { useAppTranslation as useAppTranslation5 } from "@amzn/awsmp-max-translations";
import { jsx as jsx10, jsxs as jsxs6 } from "react/jsx-runtime";
var PricingAnnotation = ({ data }) => {
  const { t } = useAppTranslation5();
  const { content } = data;
  if (!content.hasPricing) {
    return null;
  }
  const contentParts = t("pricingDisclaimerContent").split("AWS Pricing Calculator");
  return /* @__PURE__ */ jsxs6(Alert2, { type: "info", header: t("alwaysVerifyPricingEstimates"), children: [
    contentParts[0],
    /* @__PURE__ */ jsx10(Link2, { external: true, href: "https://calculator.aws/#/", children: t("awsPricingCalculator") }),
    contentParts[1]
  ] });
};

// src/results/ProvideRequirementsAnnotation/ProvideRequirementsAnnotation.tsx
import * as React7 from "react";
import Alert3 from "@cloudscape-design/components/alert";
import FileInput from "@cloudscape-design/components/file-input";
import Spinner from "@cloudscape-design/components/spinner";
import Box4 from "@cloudscape-design/components/box";
import SpaceBetween5 from "@cloudscape-design/components/space-between";
import FileTokenGroup from "@cloudscape-design/components/file-token-group";
import { useAppTranslation as useAppTranslation6 } from "@amzn/awsmp-max-translations";

// src/results/ProvideRequirementsAnnotation/ProvideRequirementsAnnotation.module.scss
var ProvideRequirementsAnnotation_module_default = {
  container: "ProvideRequirementsAnnotation_module_container",
  hiding: "ProvideRequirementsAnnotation_module_hiding"
};

// src/results/ProvideRequirementsAnnotation/ProvideRequirementsAnnotation.tsx
import { ACCEPTED_EXTENSIONS_STRING, validateFiles } from "@amzn/awsmp-max-model";
import { jsx as jsx11, jsxs as jsxs7 } from "react/jsx-runtime";
var ProvideRequirementsAnnotation = ({ uploadFiles, sendMessage }) => {
  const { t } = useAppTranslation6();
  const [files, setFiles] = React7.useState([]);
  const [uploading, setUploading] = React7.useState(false);
  const [processing, setProcessing] = React7.useState(false);
  const [uploadProgress, setUploadProgress] = React7.useState({});
  const [uploadErrors, setUploadErrors] = React7.useState({});
  const [generalError, setGeneralError] = React7.useState(null);
  const [alertType, setAlertType] = React7.useState("info");
  const [isHiding, setIsHiding] = React7.useState(false);
  const handleFileChange = async (selectedFiles) => {
    setFiles(selectedFiles);
    if (selectedFiles.length > 0) {
      const validation = validateFiles(selectedFiles);
      if (!validation.isValid) {
        setGeneralError(validation.errors.join(". "));
        setAlertType("error");
        return;
      }
      setUploading(true);
      setUploadProgress({});
      setUploadErrors({});
      setGeneralError(null);
      setAlertType("info");
      try {
        await uploadFiles(selectedFiles, {
          onProgress: (fileName, percent2) => {
            setUploadProgress((prev) => ({ ...prev, [fileName]: percent2 }));
          },
          onSuccess: (fileName) => {
            setUploadProgress((prev) => ({ ...prev, [fileName]: 100 }));
            setFiles((prev) => prev.filter((f) => f.name !== fileName));
          },
          onError: (fileName, error) => {
            setUploadErrors((prev) => ({ ...prev, [fileName]: error }));
            setAlertType("error");
          },
          onProcessing: () => {
            setUploading(false);
            setProcessing(true);
          },
          onComplete: () => {
            setProcessing(false);
            const fileNames = selectedFiles.map((f) => f.name);
            sendMessage(
              t("analyzeUploadedFiles", {
                plural: fileNames.length > 1 ? "s" : "",
                files: fileNames.join(", ")
              })
            );
            setIsHiding(true);
            setFiles([]);
            setUploadProgress({});
          }
        });
      } catch (_error) {
        setGeneralError(t("unexpectedError"));
        setAlertType("error");
      } finally {
        setUploading(false);
        setProcessing(false);
      }
    }
  };
  return /* @__PURE__ */ jsx11("div", { className: `${ProvideRequirementsAnnotation_module_default.container} ${isHiding ? ProvideRequirementsAnnotation_module_default.hiding : ""}`, children: /* @__PURE__ */ jsx11(
    Alert3,
    {
      type: alertType,
      header: t("getTailoredRecommendations"),
      action: !uploading && !processing ? /* @__PURE__ */ jsx11(
        FileInput,
        {
          ariaLabel: t("uploadRequirementsFiles"),
          multiple: true,
          value: files,
          onChange: ({ detail }) => handleFileChange(detail.value),
          accept: ACCEPTED_EXTENSIONS_STRING,
          children: "Upload files"
        }
      ) : void 0,
      children: /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "s", children: [
        /* @__PURE__ */ jsx11(Box4, { children: t("uploadFilesToTellAboutRequirements") }),
        generalError && /* @__PURE__ */ jsx11(Box4, { color: "text-status-error", children: generalError }),
        files.length > 0 && /* @__PURE__ */ jsx11(
          FileTokenGroup,
          {
            items: files.map((file) => {
              const progress2 = uploadProgress[file.name];
              const error = uploadErrors[file.name];
              return {
                file,
                loading: progress2 !== void 0 && progress2 < 100,
                errorText: error
              };
            }),
            onDismiss: ({ detail }) => {
              const removedFile = files[detail.fileIndex];
              const newFiles = files.filter((_, index) => index !== detail.fileIndex);
              setFiles(newFiles);
              setUploadErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[removedFile.name];
                return newErrors;
              });
              if (Object.keys(uploadErrors).length === 1 && uploadErrors[removedFile.name]) {
                setAlertType("info");
              }
            },
            alignment: "horizontal",
            showFileSize: true,
            showFileLastModified: true,
            showFileThumbnail: true,
            i18nStrings: {
              removeFileAriaLabel: () => t("removeFile"),
              limitShowFewer: t("showFewerFiles"),
              limitShowMore: t("showMoreFiles"),
              errorIconAriaLabel: t("errorIconAriaLabel"),
              warningIconAriaLabel: t("warningIconAriaLabel")
            }
          }
        ),
        uploading && /* @__PURE__ */ jsx11(Box4, { children: /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "xs", direction: "horizontal", alignItems: "center", children: [
          /* @__PURE__ */ jsx11(Spinner, { size: "normal" }),
          /* @__PURE__ */ jsx11(Box4, { children: t("uploadingFiles") })
        ] }) }),
        processing && /* @__PURE__ */ jsx11(Box4, { children: /* @__PURE__ */ jsxs7(SpaceBetween5, { size: "xs", direction: "horizontal", alignItems: "center", children: [
          /* @__PURE__ */ jsx11(Spinner, { size: "normal" }),
          /* @__PURE__ */ jsx11(Box4, { children: t("processingDocuments") })
        ] }) })
      ] })
    }
  ) });
};

// src/results/AskUser/AskUser.tsx
import RadioGroup from "@cloudscape-design/components/radio-group";
import SpaceBetween6 from "@cloudscape-design/components/space-between";
import Select from "@cloudscape-design/components/select";
import Button3 from "@cloudscape-design/components/button";
import Box5 from "@cloudscape-design/components/box";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Textarea from "@cloudscape-design/components/textarea";
import Checkbox from "@cloudscape-design/components/checkbox";
import * as React8 from "react";
import { MAX_ASKUSER_FIELD_LENGTH } from "@amzn/awsmp-max-model";
import { useAppTranslation as useAppTranslation7 } from "@amzn/awsmp-max-translations";
import { ClickableOptions as ClickableOptions2 } from "@amzn/awsmp-max-ux-patterns/ClickableOptions";
import { MarkdownRenderer as MarkdownRenderer4 } from "@amzn/awsmp-max-ux-patterns/MarkdownRenderer";

// src/results/AskUser/AskUser.module.scss
var AskUser_module_default = {
  checkboxOption: "AskUser_module_checkboxOption"
};

// src/results/AskUser/AskUser.tsx
import { Fragment as Fragment5, jsx as jsx12, jsxs as jsxs8 } from "react/jsx-runtime";
var AskUserMultiselect = ({ options, sendMessage, isSubmitDisabled }) => {
  const { t } = useAppTranslation7();
  const optionsValue = options || [];
  const [selectedOptions, setSelectedOptions] = React8.useState([]);
  const handleToggle = (option, checked) => {
    if (checked) {
      setSelectedOptions((prev) => [...prev, option]);
    } else {
      setSelectedOptions((prev) => prev.filter((opt) => opt !== option));
    }
  };
  const handleSubmit = () => {
    if (selectedOptions.length > 0 && !isSubmitDisabled) {
      sendMessage(JSON.stringify(selectedOptions));
    }
  };
  return /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "xs", children: [
    /* @__PURE__ */ jsx12("div", { role: "group", "aria-label": t("selectOneOrMoreOptions"), children: optionsValue.map((option, index) => /* @__PURE__ */ jsx12("div", { className: AskUser_module_default.checkboxOption, children: /* @__PURE__ */ jsx12(
      Checkbox,
      {
        checked: selectedOptions.includes(option),
        onChange: ({ detail }) => handleToggle(option, detail.checked),
        disabled: isSubmitDisabled,
        children: option
      }
    ) }, index)) }),
    /* @__PURE__ */ jsx12(Button3, { variant: "primary", onClick: handleSubmit, disabled: selectedOptions.length === 0 || isSubmitDisabled, iconName: "send", children: t("continue") })
  ] });
};
var AskUserBoolean = ({ sendMessage, isSubmitDisabled }) => {
  const { t } = useAppTranslation7();
  const [selectedValue, setSelectedValue] = React8.useState(null);
  const handleChange = (value) => {
    if (!isSubmitDisabled) {
      setSelectedValue(value);
      sendMessage(value);
    }
  };
  return /* @__PURE__ */ jsx12(
    RadioGroup,
    {
      value: selectedValue,
      onChange: ({ detail }) => handleChange(detail.value),
      items: [
        { value: "true", label: t("yes"), disabled: isSubmitDisabled },
        { value: "false", label: t("no"), disabled: isSubmitDisabled }
      ]
    }
  );
};
var AskUserElicitationForm = ({ data, toolUseId, sendMessage, isSubmitDisabled, initialData }) => {
  const { t } = useAppTranslation7();
  const { requestedSchema } = data;
  const question = data.question;
  const [formData, setFormData] = React8.useState(initialData || {});
  const [errors, setErrors] = React8.useState({});
  const [customInputFields, setCustomInputFields] = React8.useState(/* @__PURE__ */ new Set());
  if (!requestedSchema || !requestedSchema.properties) {
    return /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "l", children: [
      question && /* @__PURE__ */ jsx12(MarkdownRenderer4, { variant: "assistant", children: question }),
      /* @__PURE__ */ jsx12(Box5, { color: "text-body-secondary", children: t("loadingForm") })
    ] });
  }
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };
  const validateAndSubmit = () => {
    if (!requestedSchema) {
      return;
    }
    const newErrors = {};
    requestedSchema.required?.forEach((fieldName) => {
      if (!formData[fieldName]) {
        newErrors[fieldName] = t("formRequired");
      }
    });
    Object.entries(requestedSchema.properties).forEach(([fieldName, _property]) => {
      const value = formData[fieldName];
      if (value && typeof value === "string" && value.length > MAX_ASKUSER_FIELD_LENGTH) {
        newErrors[fieldName] = `Exceeds maximum length of ${MAX_ASKUSER_FIELD_LENGTH} characters`;
      }
      const customFieldName = `${fieldName}_custom`;
      const customValue = formData[customFieldName];
      if (customValue && typeof customValue === "string" && customValue.length > MAX_ASKUSER_FIELD_LENGTH) {
        newErrors[fieldName] = `Exceeds maximum length of ${MAX_ASKUSER_FIELD_LENGTH} characters`;
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    sendMessage([
      {
        toolOutput: {
          toolUseId,
          name: "askUser",
          output: {
            askUser: {
              responseFormat: "accept",
              data: formData
            }
          },
          status: "COMPLETE"
        }
      }
    ]);
  };
  const renderField = (fieldName, property) => {
    const value = formData[fieldName] || "";
    if ("enum" in property && property.enum) {
      const isCustomInput = value === "Other";
      if (isCustomInput) {
        const customFieldName = `${fieldName}_custom`;
        const customValue = formData[customFieldName] || "";
        const charCount2 = String(customValue).length;
        const isOverLimit2 = charCount2 > MAX_ASKUSER_FIELD_LENGTH;
        return /* @__PURE__ */ jsxs8("div", { style: { display: "flex", gap: "8px", alignItems: "flex-start" }, children: [
          /* @__PURE__ */ jsx12("div", { style: { flex: 1 }, children: /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "xxs", children: [
            /* @__PURE__ */ jsx12(
              Textarea,
              {
                value: customValue,
                onChange: ({ detail }) => handleFieldChange(customFieldName, detail.value),
                placeholder: "Please specify...",
                disabled: isSubmitDisabled,
                invalid: isOverLimit2
              }
            ),
            /* @__PURE__ */ jsxs8(Box5, { fontSize: "body-s", color: isOverLimit2 ? "text-status-error" : "text-status-inactive", children: [
              charCount2,
              " / ",
              MAX_ASKUSER_FIELD_LENGTH,
              " characters"
            ] })
          ] }) }),
          /* @__PURE__ */ jsx12(
            Button3,
            {
              iconName: "close",
              variant: "icon",
              ariaLabel: "Clear custom input",
              onClick: () => {
                handleFieldChange(fieldName, "");
                handleFieldChange(customFieldName, "");
                const newSet = new Set(customInputFields);
                newSet.delete(fieldName);
                setCustomInputFields(newSet);
              },
              disabled: isSubmitDisabled
            }
          )
        ] });
      }
      return /* @__PURE__ */ jsx12(
        Select,
        {
          selectedOption: value ? { label: value, value } : null,
          onChange: ({ detail }) => {
            const newValue = detail.selectedOption.value || "";
            handleFieldChange(fieldName, newValue);
            if (newValue === "Other") {
              const newSet = new Set(customInputFields);
              newSet.add(fieldName);
              setCustomInputFields(newSet);
            }
          },
          options: property.enum.map((val) => ({ label: String(val), value: String(val) })),
          placeholder: property.placeholder || t("selectAnOption"),
          disabled: isSubmitDisabled
        }
      );
    }
    if (property.type === "number" || property.type === "integer") {
      return /* @__PURE__ */ jsx12(
        Input,
        {
          value,
          onChange: ({ detail }) => handleFieldChange(fieldName, Number(detail.value) || ""),
          type: "number",
          placeholder: property.placeholder,
          disabled: isSubmitDisabled
        }
      );
    }
    if (property.type === "boolean") {
      return /* @__PURE__ */ jsx12(Checkbox, { checked: value || false, onChange: ({ detail }) => handleFieldChange(fieldName, detail.checked), disabled: isSubmitDisabled });
    }
    const charCount = String(value).length;
    const isOverLimit = charCount > MAX_ASKUSER_FIELD_LENGTH;
    return /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "xxs", children: [
      /* @__PURE__ */ jsx12(
        Input,
        {
          value,
          onChange: ({ detail }) => handleFieldChange(fieldName, detail.value),
          placeholder: property.placeholder,
          disabled: isSubmitDisabled,
          invalid: isOverLimit
        }
      ),
      charCount > 0 && /* @__PURE__ */ jsxs8(Box5, { fontSize: "body-s", color: isOverLimit ? "text-status-error" : "text-status-inactive", children: [
        charCount,
        " / ",
        MAX_ASKUSER_FIELD_LENGTH,
        " characters"
      ] })
    ] });
  };
  return /* @__PURE__ */ jsx12(
    Form,
    {
      actions: /* @__PURE__ */ jsx12(SpaceBetween6, { direction: "horizontal", size: "xs", children: /* @__PURE__ */ jsx12(Button3, { variant: "primary", onClick: validateAndSubmit, disabled: isSubmitDisabled, children: t("submit") }) }),
      children: /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "l", children: [
        question && /* @__PURE__ */ jsx12(MarkdownRenderer4, { variant: "assistant", children: question }),
        requestedSchema.properties && Object.entries(requestedSchema.properties).map(([fieldName, property]) => {
          const isRequired = requestedSchema.required?.includes(fieldName);
          const baseLabel = property.label || fieldName;
          const labelWithOptional = isRequired ? baseLabel : /* @__PURE__ */ jsxs8(Fragment5, { children: [
            baseLabel,
            " ",
            /* @__PURE__ */ jsx12("span", { style: { fontStyle: "italic" }, children: "- Optional" })
          ] });
          return /* @__PURE__ */ jsx12(
            FormField,
            {
              label: labelWithOptional,
              description: property.description,
              errorText: errors[fieldName],
              constraintText: isRequired ? t("required") : void 0,
              children: renderField(fieldName, property)
            },
            fieldName
          );
        })
      ] })
    }
  );
};
var isElicitationMode = (data) => {
  return data.mode === "elicitation";
};
var ResponseRenderer = ({ output, layoutManager, navigationManager }) => {
  const { t } = useAppTranslation7();
  if (output.responseFormat === "accept") {
    return null;
  }
  let responseText;
  switch (output.responseFormat) {
    case "simple": {
      if (typeof output.value === "boolean") {
        responseText = output.value ? t("yes") : t("no");
      } else if (Array.isArray(output.value)) {
        responseText = output.value.join(", ");
      } else {
        responseText = output.value;
      }
      break;
    }
    case "decline": {
      responseText = output.reason ?? t("userDeclinedToProvideInformation");
      break;
    }
  }
  if (!responseText) {
    return null;
  }
  return /* @__PURE__ */ jsx12(Text, { data: { content: { text: responseText } }, messageRole: "user", layoutManager, navigationManager });
};
var AskUser = ({ data, toolUseId, rendererAPI }) => {
  const { t } = useAppTranslation7();
  const { sendMessage, layoutManager, navigationManager } = rendererAPI;
  const inputData = data.input;
  const outputData = data.output;
  const [hasSubmitted, setHasSubmitted] = React8.useState(false);
  const handleSendMessage = React8.useCallback(
    (content) => {
      setHasSubmitted(true);
      sendMessage(content);
    },
    [sendMessage]
  );
  if (!inputData) {
    return null;
  }
  if (outputData) {
    if (isElicitationMode(inputData)) {
      return /* @__PURE__ */ jsx12(SpaceBetween6, { size: "m", children: /* @__PURE__ */ jsx12(
        AskUserElicitationForm,
        {
          data: inputData,
          toolUseId,
          sendMessage,
          isSubmitDisabled: true,
          initialData: outputData.responseFormat === "accept" ? outputData.data : void 0
        }
      ) });
    }
    const { question: question2 } = inputData;
    return /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "m", children: [
      question2 && /* @__PURE__ */ jsx12(MarkdownRenderer4, { variant: "assistant", children: question2 }),
      /* @__PURE__ */ jsx12(ResponseRenderer, { output: outputData, layoutManager, navigationManager })
    ] });
  }
  const isSubmitDisabled = hasSubmitted || !!outputData || rendererAPI.isLatestMessage !== void 0 && !rendererAPI.isLatestMessage;
  if (isElicitationMode(inputData)) {
    return /* @__PURE__ */ jsx12(
      AskUserElicitationForm,
      {
        data: inputData,
        toolUseId,
        sendMessage: handleSendMessage,
        isSubmitDisabled,
        initialData: void 0
      }
    );
  }
  const { question, responseType, relatedQuestions, options } = inputData;
  return /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "m", children: [
    question && /* @__PURE__ */ jsx12(MarkdownRenderer4, { variant: "assistant", children: question }),
    responseType === "select" && options && /* @__PURE__ */ jsx12(
      ClickableOptions2,
      {
        options,
        onSelect: handleSendMessage,
        disabled: isSubmitDisabled,
        ariaLabel: "Select an option",
        logger: rendererAPI.logger
      },
      "select"
    ),
    responseType === "multiselect" && /* @__PURE__ */ jsx12(AskUserMultiselect, { options, sendMessage: handleSendMessage, isSubmitDisabled }, "multiselect"),
    responseType === "boolean" && /* @__PURE__ */ jsx12(AskUserBoolean, { sendMessage: handleSendMessage, isSubmitDisabled }, "boolean"),
    relatedQuestions && relatedQuestions.length > 0 && /* @__PURE__ */ jsxs8(SpaceBetween6, { size: "xs", children: [
      /* @__PURE__ */ jsx12(Box5, { variant: "h3", color: "text-label", children: t("relatedQuestions") }),
      /* @__PURE__ */ jsx12(
        ClickableOptions2,
        {
          options: relatedQuestions,
          onSelect: handleSendMessage,
          disabled: isSubmitDisabled,
          ariaLabel: t("relatedQuestions"),
          logger: rendererAPI.logger
        }
      )
    ] }, "related")
  ] });
};

// src/analysis/AnalysisSteps/AnalysisSteps.tsx
import Box6 from "@cloudscape-design/components/box";
import SpaceBetween8 from "@cloudscape-design/components/space-between";
import Button4 from "@cloudscape-design/components/button";
import * as React12 from "react";
import { MessageStatus as MessageStatus2 } from "@amzn/awsmp-max-model";
import { TipCard, useTipCardTimer, AWS_MARKETPLACE_TIPS } from "@amzn/awsmp-max-ux-patterns/TipCard";

// src/analysis/AnalysisSteps/ThinkStep.tsx
import Icon from "@cloudscape-design/components/icon";
import Spinner2 from "@cloudscape-design/components/spinner";
import SpaceBetween7 from "@cloudscape-design/components/space-between";
import * as React9 from "react";
import { ProductToken } from "@amzn/awsmp-max-ux-patterns/ProductToken";
import { WebSearchToken } from "@amzn/awsmp-max-ux-patterns/WebSearchToken";

// src/analysis/AnalysisSteps/AnalysisSteps.module.scss
var AnalysisSteps_module_default = {
  fadeInScale: "AnalysisSteps_module_fadeInScale",
  shimmer: "AnalysisSteps_module_shimmer",
  thinkStep: "AnalysisSteps_module_thinkStep",
  iconColumn: "AnalysisSteps_module_iconColumn",
  textColumn: "AnalysisSteps_module_textColumn",
  thinkStepTitle: "AnalysisSteps_module_thinkStepTitle",
  shimmerText: "AnalysisSteps_module_shimmerText",
  thinkStepDescription: "AnalysisSteps_module_thinkStepDescription",
  idleMessage: "AnalysisSteps_module_idleMessage",
  loadingSpinner: "AnalysisSteps_module_loadingSpinner",
  dots: "AnalysisSteps_module_dots",
  fileTokenContainer: "AnalysisSteps_module_fileTokenContainer",
  fileToken: "AnalysisSteps_module_fileToken",
  fileLabel: "AnalysisSteps_module_fileLabel",
  productTokenContainer: "AnalysisSteps_module_productTokenContainer",
  fadeTransition: "AnalysisSteps_module_fadeTransition",
  tokenFade: "AnalysisSteps_module_tokenFade",
  analysisStepsWrapper: "AnalysisSteps_module_analysisStepsWrapper",
  analysisStepsContainer: "AnalysisSteps_module_analysisStepsContainer",
  hasTopFade: "AnalysisSteps_module_hasTopFade",
  hasBottomFade: "AnalysisSteps_module_hasBottomFade",
  scrollButtonContainer: "AnalysisSteps_module_scrollButtonContainer",
  fadeInButton: "AnalysisSteps_module_fadeInButton"
};

// src/analysis/AnalysisSteps/ThinkStep.tsx
import { jsx as jsx13, jsxs as jsxs9 } from "react/jsx-runtime";
var convertListingCardToProduct = (card) => ({
  productId: card.id,
  productName: card.title,
  vendorName: card.vendor.name,
  productLogoUrl: card.logo.url,
  badges: card.badges
});
var ThinkStep = ({ step, isStreaming = false, isLast = false, idleMessage, children }) => {
  const [currentTokenIndex, setCurrentTokenIndex] = React9.useState(0);
  const [currentWebSearchIndex, setCurrentWebSearchIndex] = React9.useState(0);
  const showLoadingState = isLast && isStreaming;
  React9.useEffect(() => {
    if (!isStreaming || !step.listingCards || step.listingCards.length <= 1)
      return;
    const interval = setInterval(() => {
      setCurrentTokenIndex((prev) => (prev + 1) % step.listingCards.length);
    }, 1e4);
    return () => clearInterval(interval);
  }, [isStreaming, step.listingCards]);
  React9.useEffect(() => {
    if (!isStreaming || !step.webSearchCards || step.webSearchCards.length <= 1)
      return;
    const interval = setInterval(() => {
      setCurrentWebSearchIndex((prev) => (prev + 1) % step.webSearchCards.length);
    }, 1e4);
    return () => clearInterval(interval);
  }, [isStreaming, step.webSearchCards]);
  return /* @__PURE__ */ jsxs9("div", { className: AnalysisSteps_module_default.thinkStep, children: [
    /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.iconColumn, children: showLoadingState ? /* @__PURE__ */ jsx13(Spinner2, { size: "normal" }) : /* @__PURE__ */ jsx13(Icon, { name: "status-positive", variant: "success" }) }),
    /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.textColumn, children: /* @__PURE__ */ jsxs9(SpaceBetween7, { direction: "vertical", size: "xs", children: [
      /* @__PURE__ */ jsxs9(SpaceBetween7, { direction: "vertical", size: "xxs", children: [
        /* @__PURE__ */ jsxs9("div", { className: `${AnalysisSteps_module_default.thinkStepTitle} ${showLoadingState ? AnalysisSteps_module_default.shimmerText : ""}`, children: [
          step.title,
          idleMessage && /* @__PURE__ */ jsxs9("span", { className: AnalysisSteps_module_default.idleMessage, children: [
            " \u2014 ",
            idleMessage
          ] })
        ] }),
        step.description && /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.thinkStepDescription, children: step.description })
      ] }),
      step.files && step.files.length > 0 && /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.fileTokenContainer, children: step.files.map((file, index) => /* @__PURE__ */ jsxs9("span", { className: AnalysisSteps_module_default.fileToken, children: [
        /* @__PURE__ */ jsx13(Icon, { name: "file", size: "small" }),
        /* @__PURE__ */ jsx13("span", { className: AnalysisSteps_module_default.fileLabel, children: file })
      ] }, index)) }),
      isStreaming && step.listingCards && step.listingCards.length > 0 && /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.productTokenContainer, children: /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.fadeTransition, children: /* @__PURE__ */ jsx13(
        ProductToken,
        {
          product: convertListingCardToProduct(step.listingCards[currentTokenIndex]),
          productUrl: `https://aws.amazon.com/marketplace/pp/${step.listingCards[currentTokenIndex].id}`
        }
      ) }) }, `token-${currentTokenIndex}`),
      isStreaming && step.webSearchCards && step.webSearchCards.length > 0 && /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.webSearchTokenContainer, children: /* @__PURE__ */ jsx13("div", { className: AnalysisSteps_module_default.fadeTransition, children: /* @__PURE__ */ jsx13(WebSearchToken, { card: step.webSearchCards[currentWebSearchIndex] }) }) }, `websearch-${currentWebSearchIndex}`),
      children
    ] }) })
  ] });
};

// src/analysis/AnalysisSteps/ThrottleList.tsx
import * as React10 from "react";
import { Fragment as Fragment7, jsx as jsx14 } from "react/jsx-runtime";
function ThrottleList({ items, delayMs = 200, renderItem, keyExtractor, isItemComplete }) {
  const completeItems = React10.useMemo(() => {
    if (!isItemComplete) {
      return items;
    }
    return items.filter((item, index) => isItemComplete(item, index));
  }, [items, isItemComplete]);
  const [displayedCount, setDisplayedCount] = React10.useState(completeItems.length);
  React10.useEffect(() => {
    if (displayedCount < completeItems.length) {
      setDisplayedCount((prev) => prev + 1);
      if (displayedCount + 1 < completeItems.length) {
        const timer = setInterval(() => {
          setDisplayedCount((prev) => {
            const nextCount = prev + 1;
            if (nextCount >= completeItems.length) {
              clearInterval(timer);
              return completeItems.length;
            }
            return nextCount;
          });
        }, delayMs);
        return () => clearInterval(timer);
      }
    } else if (displayedCount > completeItems.length) {
      setDisplayedCount(completeItems.length);
    }
  }, [completeItems.length, displayedCount, delayMs]);
  return /* @__PURE__ */ jsx14(Fragment7, { children: completeItems.slice(0, displayedCount).map((item, index) => {
    const key = keyExtractor ? keyExtractor(item, index) : String(index);
    const isLast = index === displayedCount - 1;
    return /* @__PURE__ */ jsx14(React10.Fragment, { children: renderItem(item, index, isLast) }, key);
  }) });
}

// src/analysis/AnalysisSteps/AnimatedDots.tsx
import Spinner3 from "@cloudscape-design/components/spinner";
import * as React11 from "react";
import { jsx as jsx15, jsxs as jsxs10 } from "react/jsx-runtime";
var AnimatedDots = ({ text }) => {
  const [dots, setDots] = React11.useState("");
  React11.useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === "...")
          return "";
        return prev + ".";
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return /* @__PURE__ */ jsxs10("div", { className: AnalysisSteps_module_default.loadingSpinner, children: [
    /* @__PURE__ */ jsx15(Spinner3, {}),
    text && /* @__PURE__ */ jsx15("span", { children: text }),
    /* @__PURE__ */ jsx15("span", { className: AnalysisSteps_module_default.dots, children: dots })
  ] });
};

// src/analysis/AnalysisSteps/AnalysisSteps.tsx
import { jsx as jsx16, jsxs as jsxs11 } from "react/jsx-runtime";
var AnalysisSteps = ({ data, status }) => {
  const { content } = data;
  const thinkSteps = content.thinkSteps || [];
  const containerRef = React12.useRef(null);
  const [showTopFade, setShowTopFade] = React12.useState(false);
  const [showBottomFade, setShowBottomFade] = React12.useState(false);
  const [showScrollButton, setShowScrollButton] = React12.useState(false);
  const shouldAutoScroll = React12.useRef(true);
  const isProgrammaticScroll = React12.useRef(false);
  const isStreaming = status === MessageStatus2.Streaming;
  const { showIdleState, resetTimer } = useTipCardTimer({
    delayMs: 15e3,
    enabled: isStreaming
  });
  const [tipCardShown, setTipCardShown] = React12.useState(false);
  React12.useEffect(() => {
    if (showIdleState) {
      setTipCardShown(true);
    }
  }, [showIdleState]);
  React12.useEffect(() => {
    if (thinkSteps.length > 0) {
      resetTimer();
    }
  }, [thinkSteps.length, resetTimer]);
  const updateFades = React12.useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const hasOverflow = scrollHeight > clientHeight;
      if (!hasOverflow) {
        setShowTopFade(false);
        setShowBottomFade(false);
        setShowScrollButton(false);
      } else {
        const isAtTop = scrollTop <= 10;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        setShowTopFade(!isAtTop);
        setShowBottomFade(!isAtBottom);
        setShowScrollButton(!shouldAutoScroll.current && !isAtBottom);
      }
    }
  }, []);
  React12.useEffect(() => {
    if (!shouldAutoScroll.current || !containerRef.current)
      return;
    isProgrammaticScroll.current = true;
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
    updateFades();
    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 100);
  }, [thinkSteps.length, updateFades]);
  React12.useEffect(() => {
    const container = containerRef.current;
    if (!container)
      return;
    let isUserScrolling = false;
    const handleUserGesture = () => {
      isUserScrolling = true;
    };
    const handleScroll = () => {
      updateFades();
      if (isProgrammaticScroll.current)
        return;
      if (isUserScrolling) {
        shouldAutoScroll.current = false;
        isUserScrolling = false;
        updateFades();
      }
    };
    const handleKeyDown = (e) => {
      if (container.contains(document.activeElement) || container.contains(e.target)) {
        if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) {
          handleUserGesture();
        }
      }
    };
    container.addEventListener("wheel", handleUserGesture, { passive: true });
    container.addEventListener("touchmove", handleUserGesture, { passive: true });
    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("keydown", handleKeyDown);
    updateFades();
    return () => {
      container.removeEventListener("wheel", handleUserGesture);
      container.removeEventListener("touchmove", handleUserGesture);
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [updateFades]);
  React12.useEffect(() => {
    if (isStreaming) {
      shouldAutoScroll.current = true;
      setShowScrollButton(false);
    }
  }, [isStreaming]);
  const handleScrollToBottom = React12.useCallback(() => {
    if (containerRef.current) {
      shouldAutoScroll.current = true;
      isProgrammaticScroll.current = true;
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth"
      });
      setShowScrollButton(false);
      updateFades();
      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 100);
    }
  }, [updateFades]);
  if (thinkSteps.length === 0 && !isStreaming) {
    return null;
  }
  return /* @__PURE__ */ jsx16(Box6, { children: /* @__PURE__ */ jsxs11("div", { className: AnalysisSteps_module_default.analysisStepsWrapper, children: [
    /* @__PURE__ */ jsx16(
      "div",
      {
        ref: containerRef,
        "data-testid": "analysis-steps-scroll-container",
        className: `${AnalysisSteps_module_default.analysisStepsContainer} ${showTopFade ? AnalysisSteps_module_default.hasTopFade : ""} ${showBottomFade ? AnalysisSteps_module_default.hasBottomFade : ""}`,
        children: /* @__PURE__ */ jsxs11(SpaceBetween8, { size: "xxxs", children: [
          thinkSteps.length > 0 && /* @__PURE__ */ jsx16(
            ThrottleList,
            {
              items: thinkSteps,
              delayMs: 200,
              renderItem: (step, index, isLast) => /* @__PURE__ */ jsx16(
                ThinkStep,
                {
                  step,
                  isStreaming,
                  isLast,
                  idleMessage: isLast && showIdleState ? "I'm still working, but this might take a moment." : void 0,
                  children: isLast && tipCardShown && /* @__PURE__ */ jsx16(Box6, { margin: { top: "xs" }, children: /* @__PURE__ */ jsx16(TipCard, { tips: AWS_MARKETPLACE_TIPS, autoRotateInterval: 1e4 }) })
                },
                `think-step-${index}`
              )
            }
          ),
          isStreaming && thinkSteps.length === 0 && /* @__PURE__ */ jsx16(AnimatedDots, {})
        ] })
      }
    ),
    showScrollButton && /* @__PURE__ */ jsx16("div", { className: AnalysisSteps_module_default.scrollButtonContainer, "data-testid": "analysis-steps-scroll-button", children: /* @__PURE__ */ jsx16(Button4, { variant: "normal", iconName: "angle-down", onClick: handleScrollToBottom, ariaLabel: "Scroll to bottom" }) })
  ] }) });
};
export {
  AnalysisSteps,
  AskUser,
  ComparisonActions,
  ContentBlock,
  InlineThink,
  LinkToArtifact,
  Message,
  NotificationBlock,
  PricingAnnotation,
  ProvideRequirementsAnnotation,
  SourcesList,
  Text,
  ThinkStep,
  WebSearch,
  WebSearchDocument,
  createSplitPanelArtifactComponents
};
//# sourceMappingURL=main.js.map
